export class Config {
	private readonly env: NodeJS.ProcessEnv

	constructor(override?: NodeJS.ProcessEnv) {
		this.env = override ? { ...override } : { ...process.env }
	}

	get instance() {
		return this.get('ENVIRONMENT') || 'dev'
	}

	get postgres() {
		return {
			host: this.require('POSTGRES_HOST'),
			port: this.requireNumber('POSTGRES_PORT'),
			database: this.require('POSTGRES_DATABASE'),
			user: this.get('POSTGRES_USER') || 'postgres',
			password: this.get('POSTGRES_PASSWORD') || 'postgres',
			ssl: false,
		}
	}

	get tripletex() {
		return {
			consumerToken: this.require('TRIPLETEX_CONSUMER_TOKEN'),
			manualInputConsumerToken:
				this.get('TRIPLETEX_MANUAL_INPUT_CONSUMER_TOKEN') ||
				this.require('TRIPLETEX_CONSUMER_TOKEN'),
			baseUrl: this.get('TRIPLETEX_BASEURL'),
			marketplaceSimulator: this.getBoolean('TRIPLETEX_MARKETPLACE_SIMULATOR'),
			useWebhooks: this.getBoolean('TRIPLETEX_USE_WEBHOOKS'),
			webhookBaseUrl: this.get('TRIPLETEX_WEBHOOK_BASEURL'),
		}
	}

	get powerOfficeGo() {
		return {
			applicationKey: this.get('POWER_OFFICE_GO_APPLICATION_KEY'),
			subscriptionKey: this.get('POWER_OFFICE_GO_SUBSCRIPTION_KEY'),
			baseUrl: this.get('POWER_OFFICE_GO_BASEURL'),
			baseUrlV1: this.get('POWER_OFFICE_GO_BASEURL_V1'),
			enabled: this.getBoolean('POWER_OFFICE_GO_ENABLED'),
			marketplaceSimulator: this.getBoolean(
				'POWER_OFFICE_GO_MARKETPLACE_SIMULATOR',
			),
		}
	}

	get twentyFourSevenOffice() {
		const enabled = this.getBoolean('TWENTY_FOUR_SEVEN_OFFICE_ENABLED')

		if (enabled) {
			return {
				applicationKey: this.require(
					'TWENTY_FOUR_SEVEN_OFFICE_APPLICATION_KEY',
				),
				username: this.require('TWENTY_FOUR_SEVEN_OFFICE_USERNAME'),
				password: this.require('TWENTY_FOUR_SEVEN_OFFICE_PASSWORD'),
				baseUrl: this.get('TWENTY_FOUR_SEVEN_OFFICE_BASEURL'),
				enabled: true,
			}
		}

		return {
			enabled: false,
			applicationKey:
				this.get('TWENTY_FOUR_SEVEN_OFFICE_APPLICATION_KEY') || '',
			username: this.get('TWENTY_FOUR_SEVEN_OFFICE_USERNAME') || '',
			password: this.get('TWENTY_FOUR_SEVEN_OFFICE_PASSWORD') || '',
			baseUrl: this.get('TWENTY_FOUR_SEVEN_OFFICE_BASEURL') || '',
		}
	}

	get openAI() {
		return {
			apiKey: this.require('OPENAI_API_KEY'),
		}
	}

	get mistral() {
		return {
			apiKey: this.require('MISTRAL_API_KEY'),
		}
	}

	get openExchangeRates() {
		return {
			appId: this.require('OPEN_EXCHANGE_APP_ID'),
		}
	}

	get climatiq() {
		return {
			apiKey: this.require('CLIMATIQ_API_KEY'),
		}
	}

	get documentsBucket() {
		return this.require('DOCUMENTS_BUCKET')
	}

	get saftBucket() {
		return this.require('SAFT_BUCKET')
	}

	get importSchedulingTimeout() {
		return this.getNumber('IMPORT_SCHEDULING_TIMEOUT') || 20 * 60 * 1000
	}

	get jwt() {
		return {
			secret: this.require('JWT_SECRET'),
			issuer: this.require('JWT_ISSUER'),
			audience: this.require('JWT_AUDIENCE'),
		}
	}

	get webappHostname() {
		return this.get('WEBAPP_HOSTNAME') || 'localhost'
	}

	get emails() {
		return {
			enabled: this.getBoolean('EMAILS_ENABLED'),
		}
	}

	get mailgun() {
		const domain = this.require('MAILGUN_DOMAIN')

		return {
			apiKey: this.require('MAILGUN_API_KEY'),
			domain,
			apiBaseUrl: `https://api.eu.mailgun.net/v3/${domain}`,
		}
	}

	get selfService() {
		return {
			enabled: this.getBoolean('SELF_SERVICE_ENABLED'),
		}
	}

	get posthog() {
		const apiKey = this.get('POSTHOG_API_KEY')

		if (!apiKey) {
			return {
				enabled: false,
				host: '',
				uiHost: '',
				apiKey: 'phc_not_enabled',
			}
		}

		return {
			apiKey: apiKey,
			host: this.get('POSTHOG_HOST') || 'https://eu.i.posthog.com',
			uiHost: this.get('POSTHOG_UI_HOST') || 'https://eu.posthog.com',
			enabled: true,
		}
	}

	get hubSpot() {
		const apiKey = this.get('HUBSPOT_API_KEY')
		return {
			hubId: this.get('HUBSPOT_HUB_ID') || '',
			apiKey: apiKey ? apiKey : '',
			crmBaseUrl: apiKey ? `${this.get('HUBSPOT_BASEURL')}/crm/v3` : '',
			conversationsBaseUrl: apiKey
				? `${this.get('HUBSPOT_BASEURL')}/conversations/v3`
				: '',
			apiEnabled: !!apiKey,
			dealPipeline: apiKey
				? this.get('HUBSPOT_DEAL_PIPELINE') || '491723455'
				: '',
			dealStage: apiKey ? this.get('HUBSPOT_DEAL_STAGE') || '760949466' : '',
		}
	}

	get google() {
		return {
			tagId: this.get('GOOGLE_TAG_ID'),
			mapsApiKey: this.get('GOOGLE_MAPS_API_KEY') || '',
		}
	}

	get recaptcha() {
		return {
			siteKey: this.get('RECAPTCHA_SITE_KEY') || '',
			secretKey: this.get('RECAPTCHA_SECRET_KEY'),
		}
	}

	get meta() {
		return {
			pixelId: this.get('META_PIXEL_ID'),
		}
	}

	get linkedin() {
		return {
			partnerId: this.get('LINKEDIN_PARTNER_ID'),
		}
	}

	get orgAccountApproval() {
		return this.getBoolean('ORG_ACCOUNT_APPROVAL')
	}

	get demoDataset() {
		return this.get('USE_DATASET')
	}

	get slackNotifications() {
		const enabled = this.getBoolean('SLACK_NOTIFICATIONS_ENABLED')

		if (!enabled) {
			return {
				enabled: false,
			}
		}

		return {
			enabled: true,
			webhookUrl: this.require('SLACK_WEBHOOK_URL'),
		}
	}

	get graphileWorker() {
		const host = this.require('GRAPHILE_POSTGRES_HOST')

		return {
			concurrency: this.getNumber('GRAPHILE_CONCURRENCY') || 10,
			host,
		}
	}

	get webauthnCredentials() {
		const RPID = this.get('WEBAPP_HOSTNAME') || 'localhost'
		const RP_NAME = 'Klara'
		const HOST_SETTINGS = {
			expectedOrigin: this.get('WEBAPP_HOSTNAME')
				? `https://${this.get('WEBAPP_HOSTNAME')}`
				: 'https://klara.localhost',
			expectedRPID: RPID,
			// Handle icloud (https://github.com/MasterKale/SimpleWebAuthn/issues/663)
			requireUserVerification: false,
		}

		return {
			RPID,
			RP_NAME,
			HOST_SETTINGS,
		}
	}

	protected get(key: string): string | undefined {
		return this.env[key]
	}

	protected require(key: string): string {
		const value = this.get(key)
		if (value == null || value === '' || typeof value === 'undefined')
			throw new Error(`Missing ENV config: ${key}`)
		return value
	}

	protected getBoolean(key: string): boolean {
		return /(1|true)/i.test(this.get(key) || 'false')
	}

	protected getNumber(key: string): number {
		return Number(this.get(key)) || 0
	}

	protected requireBoolean(key: string): boolean {
		return /(1|true)/i.test(this.require(key))
	}

	protected requireNumber(key: string): number {
		return Number(this.require(key))
	}
}
